<script setup>
import { ref } from 'vue'

import { getI18n } from '@/plugins/i18n'
import { DxDataGrid } from 'devextreme-vue/data-grid'

import ClipboardRowGrid from '@/components/sale/ClipboardRowGrid.vue'
import EditLockButtonsTemplate from '@/components/shared/EditLockButtonsTemplate.vue'

import * as dataGrid from '@/composables/data-grid'

import { useSaleStore } from '@/stores/sale.store'
import { useWebSiteStore } from '@/stores/web-site.store'

import { useEntityLock } from '@/services/entity-lock.service'

import { stringToColor, getInitials } from '@/lib/utils'

const props = defineProps({
  gridId: {
    type: String,
    default: null,
  },
  selectedRows: {
    type: Array,
    default: () => [],
  },
  dataSource: {
    type: [Array, Object],
    default: null,
  },
  selectMode: {
    type: Boolean,
    default: false,
  },
  addMode: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['update:selectedRows'])

const gridId = props.gridId || 'clipboard-group-grid'
const { gridRef } = dataGrid.useGridRef()
const formInstance = ref(null)

const onSelectionChanged = (e) => emit('update:selectedRows', e.selectedRowsData.length ? e.selectedRowsData : null)

const i18n = getI18n()

const saleStore = useSaleStore()
const webSiteStore = useWebSiteStore()
const { defaultDialogTitle } = dataGrid.useDefaultDialogTitle()

const entityLock = useEntityLock({
  baseLockUrl: saleStore.api.getAbsoluteUrl() + saleStore.api.clipboardGroupsUrl,
  modelName: 'ClipboardGroup',
  gridRef,
})
const rowButtons = entityLock.getEntityLockButtons({
  custom_text: {
    delete: (e) => i18n.t('common.deleteClipboardGroup', { clipboard_group: e.name }),
  }
})

const columns = [
  {
    type: 'buttons',
    buttons: [{template: 'editButtonsTemplate'}],
    width: '4rem',
    visibleIndex: 0,
    fixed: false
  },
  {
    caption: i18n.t('common.name'),
    dataField: 'name',
    validationRules: [{ type: 'required' }],
  },
  {
    caption: i18n.t('common.color'),
    dataField: 'color',
    cellTemplate: 'colorCellTemplate',
    validationRules: [{ type: 'required' }],
  },
  {
    caption: i18n.t('common.user'),
    cellTemplate: 'userCellTemplate',
    dataField: 'username',
  },
]
const editing = dataGrid.useDefaultEditing(i => defaultDialogTitle(i, i18n.t('common.clipboard')), {
  colCount: 1,
  items: [
    {
      itemType: 'group',
      items: [
        {
          itemType: 'group',
          caption: i18n.t('common.clipboard'),
          colCount: 4,
          items: [
            { dataField: 'name', colSpan: 2 },
            { dataField: 'color', editorType: 'dxColorBox' },
          ]
        }
      ]
    }
  ],
  onInitialized: (e) => { formInstance.value = e.component },
}, {
  width: '60rem',
  entityLock: entityLock,
  allowAdding: true,
  allowUpdating: true,
  allowDeleting: true,
})

const onSaving = (e) => {
  if (e.changes?.[0]?.type === 'insert') {
    e.changes[0].data.username = webSiteStore.user.name
  }
}

const gridOptions = dataGrid.useDefaultGridOptions({
  id: gridId,
  dataSource: props.dataSource || dataGrid.useODataSource(saleStore.api.getAbsoluteUrl() + saleStore.api.clipboardGroupsUrl),
  columns: columns,
  editing: editing,
  masterDetail: {
    enabled: true,
    template: 'master-detail',
  },
  height: props.gridId ? '100%' : '90vh',
  onSaving: onSaving,
})

if (props.selectMode) {
  gridOptions.selection = { mode: 'single', showCheckBoxesMode: 'always', selectAllMode: 'page' }
  gridOptions.columnChooser = { enabled: false }
  gridOptions.paging = { enabled: true, pageSize: 10 }
  gridOptions.focusedRowEnabled = true
  gridOptions.masterDetail = { enabled: false }
  gridOptions.onSelectionChanged = onSelectionChanged
  gridOptions.onToolbarPreparing = (e) => { e.toolbarOptions.visible = props.addMode }
}
</script>

<template>
  <dx-data-grid
    ref="gridRef"
    v-bind="gridOptions"
  >
    <template #colorCellTemplate="{ data }">
      <div style="display: flex; align-items: center; gap: 8px;">
        <div
          :style="{
            width: '16px',
            height: '16px',
            backgroundColor: data.data.color,
            border: '1px solid #ccc',
          }"
        />
        <span>{{ data.data.color }}</span>
      </div>
    </template>
    <template #userCellTemplate="{ data }">
      <q-avatar
        size="28px"
        font-size="12px"
        text-color="white"
        :style="{ backgroundColor: stringToColor(getInitials(data?.data?.username)) }"
      >
        {{ getInitials(data?.data?.username) }}
      </q-avatar>
    </template>
    <template
      #editButtonsTemplate="{ data: cellInfo }"
    >
      <edit-lock-buttons-template
        :entity-name="'ClipboardGroup'"
        :cell-info="cellInfo"
        :grid="gridRef"
        :entity-lock="entityLock"
        :is-locked-available="true"
        :buttons="rowButtons"
      />
    </template>
    <template #master-detail="{ data: templateOptions }">
      <clipboard-row-grid
        :group="templateOptions.data?.id"
      />
    </template>
  </dx-data-grid>
</template>
